import { useEffect } from "react";

import { useTranslation } from "react-i18next";

import { ModalInfoType, ModalStatus } from "@data-types";
import { useRootAppStore } from "@hooks";
import { useGenerateNecessaryCookiesList } from "@hooks/useGenerateNecessaryCookiesList";
import { getCurrentCulture } from "@utils";
import { COOKIE_CONSENT_REVISION } from "@utils/analytics/cookieConsentConfig";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import { observer } from "mobx-react";
import * as Cookie from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";

const GA_COOKIE_PATTERNS = [/^_ga/];
const FBP_COOKIE_PATTERNS = [/^_fbp/];
const UET_COOKIE_PATTERNS = [/^_uet/];

const getParentCookieDomains = (): string[] => {
	const parts = location.hostname.split(".");
	const domains: string[] = [];

	for (let i = 1; i < parts.length - 1; i++) {
		domains.push(parts.slice(i).join("."));
	}

	return domains;
};

const eraseCookiesOnAllDomains = (patterns: RegExp[]) => {
	Cookie.eraseCookies(patterns);

	getParentCookieDomains().forEach((domain) =>
		Cookie.eraseCookies(patterns, "/", domain)
	);
};

const autoClearCookiesOnAllDomains = (patterns: RegExp[]) => [
	...patterns.map((name) => ({ name })),
	...getParentCookieDomains().flatMap((domain) =>
		patterns.map((name) => ({ name, domain }))
	),
];

export default observer(function CookieConsent() {
	const culture = getCurrentCulture();
	const { t } = useTranslation();

	if (isCapacitorPlatform()) {
		return null;
	}

	const {
		cookieConsentStore: { updateModalStatus },
	} = useRootAppStore();

	const generatedCookies = useGenerateNecessaryCookiesList();

	useEffect(() => {
		const UET_LOCAL_STORAGE_KEYS = [
			"_uetsid",
			"_uetsid_exp",
			"_uetvid",
			"_uetvid_exp",
		];

		const eraseUetCookies = () => {
			eraseCookiesOnAllDomains(UET_COOKIE_PATTERNS);
		};

		const syncConsentMode = () => {
			const adsAccepted = Cookie.acceptedCategory("ads");
			const analyticsAccepted = Cookie.acceptedCategory("analytics");
			const adsState = adsAccepted ? "granted" : "denied";

			window.uetq?.push("consent", "update", {
				ad_storage: adsState,
			});

			window.gtag?.("consent", "update", {
				ad_storage: adsState,
				ad_user_data: adsState,
				ad_personalization: adsState,
				analytics_storage: analyticsAccepted ? "granted" : "denied",
			});

			if (!adsAccepted) {
				eraseUetCookies();
				UET_LOCAL_STORAGE_KEYS.forEach((k) =>
					localStorage.removeItem(k)
				);
			}
		};

		Cookie.run({
			root: document.body,
			onModalShow: (modalInfo: ModalInfoType) => {
				updateModalStatus(modalInfo, ModalStatus.SHOW);
			},

			onModalHide: (modalInfo: ModalInfoType) => {
				updateModalStatus(modalInfo, ModalStatus.HIDE);
			},

			onConsent: () => {
				syncConsentMode();
			},

			onChange: () => {
				syncConsentMode();
			},

			revision: COOKIE_CONSENT_REVISION,

			autoShow: true,

			manageScriptTags: true,

			guiOptions: {
				consentModal: {
					layout: "box wide",
					position: "bottom right",
					equalWeightButtons: true,
					flipButtons: false,
				},
				preferencesModal: {
					layout: "box",
					equalWeightButtons: true,
					flipButtons: false,
				},
			},

			categories: {
				necessary: {
					enabled: true,
					readOnly: true,
					autoClear: {
						cookies: [
							{
								name: "cc_cookie",
							},
							{
								name: "lng",
							},
						],
					},
				},
				analytics: {
					enabled: true,
					readOnly: false,
					autoClear: {
						reloadPage: true,
						cookies:
							autoClearCookiesOnAllDomains(GA_COOKIE_PATTERNS),
					},
				},
				ads: {
					enabled: true,
					readOnly: false,
					autoClear: {
						reloadPage: true,
						cookies: autoClearCookiesOnAllDomains([
							...FBP_COOKIE_PATTERNS,
							...UET_COOKIE_PATTERNS,
						]),
					},
				},
			},

			language: {
				default: "en",
				translations: {
					en: {
						consentModal: {
							title: t("COOKIE.CONSENT.MODAL_TITLE"),
							description: t("COOKIE.CONSENT.MODAL_DESCRIPTION"),
							acceptAllBtn: t("COOKIE.CONSENT.ACCEPT_ALL_BTN"),
							showPreferencesBtn: t(
								"COOKIE.CONSENT.SHOW_PREFERENCES_BTN"
							),
						},
						preferencesModal: {
							title: t("COOKIE.PREFERENCES.MODAL_TITLE"),
							acceptAllBtn: t(
								"COOKIE.PREFERENCES.ACCEPT_ALL_BTN"
							),
							savePreferencesBtn: t(
								"COOKIE.PREFERENCES.SAVE_PREFERENCES_BTN"
							),
							closeIconLabel: t(
								"COOKIE.PREFERENCES.CLOSE_MODAL_BTN"
							),
							sections: [
								{
									title:
										t("COOKIE.PREFERENCES.SECTIONS.TITLE") +
										" 📢",
									description:
										t(
											"COOKIE.PREFERENCES.SECTIONS.DESCRIPTION"
										) +
										`<a href='/${culture}/info/privacy'> ${t(
											"COOKIE.PREFERENCES.SECTIONS.PRIVACY_POLICY"
										)}.</a>`,
								},
								{
									title: t(
										"COOKIE.PREFERENCES.NECESSARY_COOKIES.TITLE"
									),
									description: t(
										"COOKIE.PREFERENCES.NECESSARY_COOKIES.DESCRIPTION"
									),
									linkedCategory: "necessary",
									cookieTable: {
										headers: {
											name: t(
												"COOKIE.TABLE_HEADERS.NAME"
											),
											domain: t(
												"COOKIE.TABLE_HEADERS.DOMAIN"
											),
											expiration: t(
												"COOKIE.TABLE_HEADERS.EXPIRATION"
											),
											desc: t(
												"COOKIE.TABLE_HEADERS.DESCRIPTION"
											),
										},
										body: generatedCookies,
									},
								},
								{
									title: t(
										"COOKIE.PREFERENCES.PERFORMANCE_AND_ANALYTICS_COOKIES.TITLE"
									),
									description: t(
										"COOKIE.PREFERENCES.PERFORMANCE_AND_ANALYTICS_COOKIES.DESCRIPTION"
									),
									linkedCategory: "analytics",
									cookieTable: {
										headers: {
											name: t(
												"COOKIE.TABLE_HEADERS.NAME"
											),
											domain: t(
												"COOKIE.TABLE_HEADERS.DOMAIN"
											),
											expiration: t(
												"COOKIE.TABLE_HEADERS.EXPIRATION"
											),
											desc: t(
												"COOKIE.TABLE_HEADERS.DESCRIPTION"
											),
										},
										body: [
											{
												name: "^_ga",
												domain: location.hostname,
												expiration:
													"400 " +
													t(
														"COOKIE.TABLE_BODY.EXPIRATION"
													),
												desc: t(
													"COOKIE.PREFERENCES.PERFORMANCE_AND_ANALYTICS_COOKIES.TABLE_BODY_GA.DESCRIPTION"
												),
											},
											{
												name: "_ga_",
												domain: location.hostname,
												expiration:
													"400 " +
													t(
														"COOKIE.TABLE_BODY.EXPIRATION"
													),
												desc: t(
													"COOKIE.PREFERENCES.PERFORMANCE_AND_ANALYTICS_COOKIES.TABLE_BODY_GA_.DESCRIPTION"
												),
											},
										],
									},
								},
								{
									title: t(
										"COOKIE.PREFERENCES.ADVERTISEMENT_AND_TARGETING_COOKIES.TITLE"
									),
									description: t(
										"COOKIE.PREFERENCES.ADVERTISEMENT_AND_TARGETING_COOKIES.DESCRIPTION"
									),
									linkedCategory: "ads",
									cookieTable: {
										headers: {
											name: t(
												"COOKIE.TABLE_HEADERS.NAME"
											),
											domain: t(
												"COOKIE.TABLE_HEADERS.DOMAIN"
											),
											expiration: t(
												"COOKIE.TABLE_HEADERS.EXPIRATION"
											),
											desc: t(
												"COOKIE.TABLE_HEADERS.DESCRIPTION"
											),
										},
										body: [
											{
												name: "_fbp",
												domain: location.hostname,
												expiration:
													"90 " +
													t(
														"COOKIE.TABLE_BODY.EXPIRATION"
													),
												desc: t(
													"COOKIE.PREFERENCES.ADVERTISEMENT_AND_TARGETING_COOKIES.TABLE_BODY_FBP.DESCRIPTION"
												),
											},
											{
												name: "_uetsid",
												domain: location.hostname,
												expiration:
													"1 " +
													t(
														"COOKIE.TABLE_BODY.EXPIRATION"
													),
												desc: t(
													"COOKIE.PREFERENCES.ADVERTISEMENT_AND_TARGETING_COOKIES.TABLE_BODY_UETSID.DESCRIPTION"
												),
											},
											{
												name: "_uetvid",
												domain: location.hostname,
												expiration:
													"390 " +
													t(
														"COOKIE.TABLE_BODY.EXPIRATION"
													),
												desc: t(
													"COOKIE.PREFERENCES.ADVERTISEMENT_AND_TARGETING_COOKIES.TABLE_BODY_UETVID.DESCRIPTION"
												),
											},
										],
									},
								},
								{
									title: t(
										"COOKIE.PREFERENCES.MORE_INFORMATION.TITLE"
									),
									description:
										t(
											"COOKIE.PREFERENCES.MORE_INFORMATION.DESCRIPTION"
										) +
										`<a href='/${culture}/info/contact'>${t(
											"COOKIE.PREFERENCES.MORE_INFORMATION.CONTACT_US"
										)}.</a>`,
								},
							],
						},
					},
				},
			},
		});

		//if category not accepted and cookie exists
		if (!Cookie.acceptedCategory("analytics")) {
			eraseCookiesOnAllDomains(GA_COOKIE_PATTERNS);
		}

		if (!Cookie.acceptedCategory("ads")) {
			eraseCookiesOnAllDomains(FBP_COOKIE_PATTERNS);

			eraseUetCookies();

			UET_LOCAL_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
		}
	}, []);

	return null;
});
